import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import {
  buildTransferBalanceParams,
  buildTransferParams,
  fiveDecimalTransfer,
  getTransferAmountError,
  isMarginInvolved,
  isMarginWallet,
  isUsdtOnlyTransferInvolved,
  findUsdtCurrency,
  filterTransferCurrencies,
  resolveMarginCurrencyId,
  transferSourceHint,
} from "./walletTransferHelpers";

/**
 * Unified wallet transfer state + handlers (GET transfer-balance, POST transfer).
 * Used by WalletPage, AssetOverview, FuturesAccountPage, CrossMarginAccountPage, MarginAccountPage.
 */
export function useWalletTransfer({
  initialFrom = "spot",
  initialTo = "",
  onTransferSuccess,
  loadWalletTypesOnMount = true,
} = {}) {
  const [fromWalletType, setFromWalletType] = useState(initialFrom);
  const [toWalletType, setToWalletType] = useState(initialTo);
  const [availableWalletTypes, setAvailableWalletTypes] = useState([]);
  const [walletLabelsMap, setWalletLabelsMap] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState({});
  const [currencyData, setCurrencyData] = useState([]);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferable, setTransferable] = useState("0");
  const [transferSource, setTransferSource] = useState(null);
  const [marginPairs, setMarginPairs] = useState([]);
  const [selectedMarginPair, setSelectedMarginPair] = useState(null);
  const [marginAssetType, setMarginAssetType] = useState("base");
  const [marginPairDropOpen, setMarginPairDropOpen] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const balanceRequestRef = useRef(0);

  const marginInvolved = isMarginInvolved(fromWalletType, toWalletType);
  const usdtOnlyInvolved = isUsdtOnlyTransferInvolved(fromWalletType, toWalletType);

  const transferCurrencyList = useMemo(
    () => filterTransferCurrencies(currencyData, fromWalletType, toWalletType),
    [currencyData, fromWalletType, toWalletType]
  );

  /** Isolated margin: currency comes from pair + base/quote — never the global coin picker. */
  const effectiveCurrencyId = useMemo(() => {
    if (marginInvolved && selectedMarginPair) {
      return resolveMarginCurrencyId(selectedMarginPair, marginAssetType, currencyData);
    }
    return selectedCurrency?.currency_id ?? null;
  }, [marginInvolved, selectedMarginPair, marginAssetType, currencyData, selectedCurrency?.currency_id]);

  const sourceHint = useMemo(() => transferSourceHint(transferSource), [transferSource]);

  const loadWalletTypes = useCallback(async () => {
    try {
      const res = await AuthService.availableWalletTypes();
      if (!res?.success) return;
      const raw = Array.isArray(res.data) ? res.data : [];
      const labelsMap = {};
      const types = raw
        .map((w) => {
          if (typeof w === "string") return w;
          if (w?.type) {
            if (w.label || w.name) labelsMap[w.type] = w.label || w.name;
            return w.type;
          }
          return null;
        })
        .filter(Boolean);
      if (types.length) {
        setAvailableWalletTypes(types);
        setWalletLabelsMap(labelsMap);
        setToWalletType((prev) => {
          if (prev && types.includes(prev)) return prev;
          return types.find((t) => t !== initialFrom) || types[0] || "";
        });
      }
    } catch {
      /* silent */
    }
  }, [initialFrom]);

  const loadCurrencyList = useCallback(async (walletType) => {
    const wt = walletType || fromWalletType;
    if (!wt || isMarginWallet(wt)) {
      try {
        const res = await AuthService.getUserfunds("spot");
        if (res?.success) setCurrencyData(res.data || []);
      } catch {
        /* silent */
      }
      return;
    }
    try {
      const res = await AuthService.getUserfunds(wt);
      if (res?.success) setCurrencyData(res.data || []);
    } catch {
      /* silent */
    }
  }, [fromWalletType]);

  const refreshTransferBalance = useCallback(async () => {
    const params = buildTransferBalanceParams({
      fromWalletType,
      toWalletType,
      currencyId: effectiveCurrencyId,
      selectedMarginPair,
      marginAssetType,
    });
    if (!params) {
      setTransferable("0");
      setTransferSource(null);
      setBalanceLoading(false);
      return;
    }

    const requestId = ++balanceRequestRef.current;
    setBalanceLoading(true);
    try {
      const res = await AuthService.getTransferBalance(params);
      if (requestId !== balanceRequestRef.current) return;
      if (res?.success && res.data) {
        setTransferable(res.data.transferable ?? "0");
        setTransferSource(res.data.source ?? null);
      } else {
        setTransferable("0");
        setTransferSource(null);
      }
    } catch {
      if (requestId !== balanceRequestRef.current) return;
      setTransferable("0");
      setTransferSource(null);
    } finally {
      if (requestId === balanceRequestRef.current) {
        setBalanceLoading(false);
      }
    }
  }, [fromWalletType, toWalletType, effectiveCurrencyId, selectedMarginPair, marginAssetType]);

  const handleInterchangeWallet = useCallback(() => {
    setFromWalletType(toWalletType);
    setToWalletType(fromWalletType);
    setTransferAmount("");
  }, [fromWalletType, toWalletType]);

  const handleTransfer = useCallback(async () => {
    if (!fromWalletType || !toWalletType) {
      alertErrorMessage("Please select source and destination wallets");
      return;
    }
    if (marginInvolved && !selectedMarginPair?.pair_id) {
      alertErrorMessage("Please select a margin pair");
      return;
    }
    if (!effectiveCurrencyId) {
      alertErrorMessage(
        marginInvolved ? "Please select pair and asset (base or quote)" : "Please select a coin"
      );
      return;
    }
    const amt = Number(transferAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      alertErrorMessage("Please enter a valid amount");
      return;
    }
    const amountError = getTransferAmountError(transferAmount, transferable);
    if (amountError) {
      alertErrorMessage(amountError);
      return;
    }

    const params = buildTransferParams({
      fromWalletType,
      toWalletType,
      currencyId: effectiveCurrencyId,
      amount: transferAmount,
      selectedMarginPair,
      marginAssetType,
    });
    if (!params) {
      alertErrorMessage("Please fill all required fields");
      return;
    }

    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.walletTransferUnified(params);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Transfer successful");
        setTransferAmount("");
        await refreshTransferBalance();
        await onTransferSuccess?.(result);
      } else {
        alertErrorMessage(result?.message || result?.error?.message || "Transfer failed");
      }
    } catch (e) {
      alertErrorMessage(e?.message || "Transfer failed");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, [
    fromWalletType,
    toWalletType,
    marginInvolved,
    selectedMarginPair,
    effectiveCurrencyId,
    transferAmount,
    transferable,
    marginAssetType,
    refreshTransferBalance,
    onTransferSuccess,
  ]);

  const openTransferModal = useCallback(({ from, to, currency } = {}) => {
    if (from) setFromWalletType(from);
    if (to) setToWalletType(to);
    if (currency) setSelectedCurrency(currency);
    else {
      const usdt = (currencyData || []).find(
        (c) => String(c.short_name || "").toUpperCase() === "USDT"
      );
      if (usdt) setSelectedCurrency(usdt);
    }
    setTransferAmount("");
  }, [currencyData]);

  useEffect(() => {
    if (loadWalletTypesOnMount) void loadWalletTypes();
  }, [loadWalletTypesOnMount, loadWalletTypes]);

  useEffect(() => {
    void loadCurrencyList(fromWalletType);
  }, [fromWalletType, loadCurrencyList]);

  useEffect(() => {
    if (!usdtOnlyInvolved) return;
    const usdt = findUsdtCurrency(currencyData);
    if (usdt) setSelectedCurrency(usdt);
  }, [usdtOnlyInvolved, fromWalletType, toWalletType, currencyData]);

  useEffect(() => {
    if (!marginInvolved) return;
    AuthService.marginAccounts()
      .then((res) => {
        if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
          setMarginPairs(res.data);
          setSelectedMarginPair((prev) => prev ?? res.data[0]);
        }
      })
      .catch(() => {});
  }, [marginInvolved]);

  useEffect(() => {
    void refreshTransferBalance();
  }, [refreshTransferBalance]);

  return {
    fromWalletType,
    setFromWalletType,
    toWalletType,
    setToWalletType,
    availableWalletTypes,
    walletLabelsMap,
    selectedCurrency,
    setSelectedCurrency,
    currencyData,
    setCurrencyData,
    transferAmount,
    setTransferAmount,
    transferable,
    balanceLoading,
    transferSource,
    sourceHint,
    marginPairs,
    selectedMarginPair,
    setSelectedMarginPair,
    marginAssetType,
    setMarginAssetType,
    marginPairDropOpen,
    setMarginPairDropOpen,
    marginInvolved,
    handleInterchangeWallet,
    handleTransfer,
    refreshTransferBalance,
    loadCurrencyList,
    loadWalletTypes,
    openTransferModal,
    fiveDecimal: fiveDecimalTransfer,
    setAvailableWalletTypes,
    setWalletLabelsMap,
  };
}
